<template lang="pug">
div(class="swap-session-container")

    //- Step indicator
    div(class="step-bar")
        div(class="step-bar-inner")
            div(class="step-item" :class="{active: step >= 1, done: step > 1}")
                div(class="step-bubble") 1
                span(class="step-label") Pick session
            div(class="step-connector" :class="{active: step > 1}")
            div(class="step-item" :class="{active: step >= 2}")
                div(class="step-bubble") 2
                span(class="step-label") Pick replacement

    //- Step 1
    div(v-if="step === 1" class="step-content" data-tour="swap-pick")

        div(class="month-pill d-flex align-items-center justify-content-between mb-4")
            b-button(size="sm" variant="outline-primary" class="month-btn" @click="changeMonth(-1)" :disabled="loadingDates || !canGoPrev")
                b-icon(icon="chevron-left")
                |  Prev
            span(class="month-label")
                b-icon(icon="calendar3" class="mr-2")
                | {{ monthDisplay }}
            b-button(size="sm" variant="outline-primary" class="month-btn" @click="changeMonth(1)" :disabled="loadingDates")
                |  Next
                b-icon(icon="chevron-right")

        div(v-if="loadingDates" class="empty-state")
            b-spinner(variant="primary" style="width:2.5rem;height:2.5rem")
            p(class="mt-3 text-muted") Loading your sessions…

        div(v-else-if="!scheduledDates.length" class="empty-state")
            div(class="empty-icon-wrap")
                b-icon(icon="calendar-x" font-scale="2.5")
            p(class="mt-3 mb-0 fw-600") No sessions this month
            small(class="text-muted") Try the next month →
            div(v-if="excludedDates.length" class="excluded-note mt-3")
                b-icon(icon="calendar-minus" class="mr-1")
                | Days excluded: {{ excludedText(excludedDates) }}

        div(v-else class="dates-scroll-container")
            div(
                v-for="(group, dayName) in groupedScheduledDates"
                :key="dayName"
                class="day-group mb-4"
            )
                div(class="day-group-header")
                    span(class="day-pill" :style="dayPillStyle(dayName)") {{ dayName }}

                div(class="date-strip")
                    div(
                        v-for="dateInfo in group"
                        :key="dateInfo.scheduleId + dateInfo.date"
                        class="date-chip"
                        :class="chipClass(dateInfo, 'original')"
                        :style="chipAccent(dateInfo, dayName)"
                        @click="selectOriginalDate(dateInfo)"
                    )
                        div(class="chip-status-icon" v-if="dateInfo.isSwapped || dateInfo.hasMC")
                            b-icon(:icon="dateInfo.isSwapped ? 'arrow-repeat' : 'file-medical'" font-scale="0.9")
                        div(class="chip-check" v-else-if="selectedOriginalDate && selectedOriginalDate.date === dateInfo.date && selectedOriginalDate.scheduleId === dateInfo.scheduleId")
                            b-icon(icon="check-circle-fill" font-scale="0.95")

                        div(class="chip-date") {{ shortDate(dateInfo.date) }}
                        div(class="chip-time") {{ shortTime(dateInfo.time) }}

                        div(v-if="dateInfo.isSwapped" class="chip-badge chip-badge--swapped") ({{ dateInfo.swapInfo.replacementDate ? formatDateMini(dateInfo.swapInfo.replacementDate) : '' }}) 
                        div(v-else-if="dateInfo.hasMC" class="chip-badge chip-badge--mc") MC

        div(v-if="!loadingDates && scheduledDates.length && excludedDates.length" class="excluded-note")
            b-icon(icon="calendar-minus" class="mr-1")
            | Days excluded: {{ excludedText(excludedDates) }}

        div(class="cta-bar" v-if="!loadingDates && scheduledDates.length")
            small(class="text-muted") Tap a session to pick its replacement date.
    
    //- Step 2
    div(v-if="step === 2" class="step-content" data-tour="swap-replacement")

        div(class="swap-banner mb-4")
            b-button(size="sm" variant="link" @click="goBack" class="back-btn p-0 mb-2")
                b-icon(icon="chevron-left")
                |  Back
            div(class="swap-from-to")
                div(class="from-block")
                    div(class="from-to-label") Swapping from
                    div(class="from-to-date") {{ formatDate(selectedOriginalDate.date) }}
                    div(class="from-to-sched") {{ friendlySession(selectedOriginalDate) }}
                b-icon(icon="arrow-right" font-scale="1.6" class="swap-arrow mx-2")
                div(class="to-block")
                    div(class="from-to-label") To
                    div(class="from-to-date" :class="{'placeholder-text': !selectedReplacementDate}") {{ selectedReplacementDate ? formatDate(selectedReplacementDate.date) : 'Tap a date below' }}
                    div(class="from-to-sched") {{ selectedReplacementDate ? friendlySession(selectedReplacementDate) : '' }}

        div(v-if="!loadingReplacements && replacementWindow" class="window-hint mb-3")
            b-icon(icon="info-circle" class="mr-1")
            | You can reschedule to any available session below

        div(v-if="loadingReplacements" class="empty-state")
            b-spinner(variant="success" style="width:2.5rem;height:2.5rem")
            p(class="mt-3 text-muted") Finding available dates\u2026

        div(v-else-if="!replacementDates.length" class="empty-state")
            div(class="empty-icon-wrap empty-icon-wrap--warning")
                b-icon(icon="calendar-x" font-scale="2.5")
            p(class="mt-3 mb-0 fw-600") No slots available
            small(v-if="replacementWindow" class="text-muted") No open replacement sessions between {{ replacementWindow.start }} and {{ replacementWindow.end }}
            small(v-else class="text-muted") No replacement sessions available in the allowed window
            div(v-if="excludedReplacements.length" class="excluded-note mt-3")
                b-icon(icon="calendar-minus" class="mr-1")
                | Days excluded: {{ excludedText(excludedReplacements) }}

        div(v-else class="dates-scroll-container")
            div(
                v-for="(group, dayName) in groupedReplacementDates"
                :key="dayName"
                class="day-group mb-4"
            )
                div(class="day-group-header")
                    span(class="day-pill" :style="dayPillStyle(dayName)") {{ dayName }}

                div(class="date-strip")
                    div(
                        v-for="dateInfo in group"
                        :key="dateInfo.scheduleId + dateInfo.date"
                        class="date-chip"
                        :class="chipClass(dateInfo, 'replacement')"
                        :style="chipAccent(dateInfo, dayName)"
                        @click="selectReplacementDate(dateInfo)"
                    )
                        div(class="chip-status-icon" v-if="dateInfo.isFullyBooked")
                            b-icon(icon="lock-fill" font-scale="0.9")
                        div(class="chip-check" v-else-if="selectedReplacementDate && selectedReplacementDate.date === dateInfo.date && selectedReplacementDate.scheduleId === dateInfo.scheduleId")
                            b-icon(icon="check-circle-fill" font-scale="0.95")

                        div(class="chip-date") {{ shortDate(dateInfo.date) }}
                        div(class="chip-time") {{ shortTime(dateInfo.time) }}
                        div(v-if="langTag(dateInfo.scheduleName)" class="chip-lang") {{ langTag(dateInfo.scheduleName) }}

                        div(v-if="!dateInfo.isFullyBooked" class="chip-slots")
                            b-icon(icon="people-fill" font-scale="0.75" class="mr-1")
                            | {{ dateInfo.availableSlots }}
                        div(v-else class="chip-badge chip-badge--full") full

        div(v-if="!loadingReplacements && replacementDates.length && excludedReplacements.length" class="excluded-note")
            b-icon(icon="calendar-minus" class="mr-1")
            | Days excluded: {{ excludedText(excludedReplacements) }}

    b-modal(v-model="showSuccessModal" title="" hide-footer centered)
        div(class="text-center py-4 px-3")
            LottieAnimation(:animation-data="success" :autoPlay="true" :loop="false" :speed="1" class="w-50 mx-auto")
            h5(class="mt-2 fw-700") Swap Request Submitted!
            p(class="text-muted small") Your request has been recorded — it's listed under "My Swaps".
            b-button(variant="primary" class="px-5 mt-1" @click="resetForm") Done

    b-modal(v-model="showGenderModal" title="Client Gender Required" hide-footer no-close-on-backdrop no-close-on-esc centered)
        div(class="text-center py-2")
            b-icon(icon="person-badge" font-scale="3" variant="primary" class="mb-3")
            p(class="mb-4") To show appropriate replacement sessions, please select the client's gender.
            div(class="d-flex justify-content-center")
                b-button.mx-2(
                    :variant="selectedGender === 0 ? 'primary' : 'outline-primary'"
                    class="px-4"
                    @click="selectedGender = 0"
                ) Male
                b-button(
                    :variant="selectedGender === 1 ? 'primary' : 'outline-primary'"
                    class="px-4"
                    @click="selectedGender = 1"
                ) Female
            b-button(
                class="mt-4 w-100"
                variant="success"
                :disabled="selectedGender == null || savingGender"
                @click="saveGender"
            )
                b-spinner(small v-if="savingGender")
                span(v-else) Save & Continue
</template>

<script>
import dayjs from 'dayjs';
import errorModal from '@/mixins/errorModal';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
import LottieAnimation from 'lottie-web-vue';
import successAnimation from '@/assets/success.json';

export default {
    mixins: [errorModal],
    components: {
        LottieAnimation
    },
    props: {
        userId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            success: successAnimation,
            step: 1, // 1: select original, 2: select replacement
            currentYear: dayjs().year(),
            currentMonth: dayjs().month() + 1,
            scheduledDates: [],
            replacementDates: [],
            // Session dates the API left out and why — public holidays and DSG
            // off days ([{ date, reason, kind }]) for the month / the swap window.
            excludedDates: [],
            excludedReplacements: [],
            replacementRange: null, // { originalDate, windowStart, windowEnd } window returned by the API
            selectedOriginalDate: null,
            selectedReplacementDate: null,
            loadingDates: false,
            loadingReplacements: false,
            submitting: false,
            showSuccessModal: false,
            showGenderModal: false,
            savingGender: false,
            selectedGender: null
        };
    },
    computed: {
        monthDisplay() {
            return dayjs(`${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`).format('MMMM YYYY');
        },
        // "25/12/2026 (Christmas Day), 18/11/2026 (DSG off day · Centre Closed)"
        excludedText() {
            return (list) => (list || [])
                .map((e) => `${dayjs(e.date).format('DD/MM/YYYY')} (${e.reason})`)
                .join(', ');
        },
        canGoPrev() {
            // Don't allow navigating to months before the current real month —
            // past sessions can't be swapped anyway (only future sessions are selectable).
            const displayedMonth = dayjs(`${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`);
            return displayedMonth.isAfter(dayjs().startOf('month'));
        },
        replacementWindow() {
            if (!this.replacementRange) return null;
            // Swap window (from the API): after the original session and after this coming weekend → swapWindowDays after the original (default 30, staff can set a per-client window).
            const start = this.replacementRange.windowStart || this.replacementRange.originalDate;
            const end = this.replacementRange.windowEnd || this.replacementRange.maxDate;
            if (!start || !end) return null;
            return {
                start: dayjs(start).format('MMM D'),
                end: dayjs(end).format('MMM D, YYYY')
            };
        },
        groupedScheduledDates() {
            const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const grouped = {};
            
            this.scheduledDates.forEach(dateInfo => {
                const dayName = dateInfo.day || dayjs(dateInfo.date).format('dddd');
                if (!grouped[dayName]) {
                    grouped[dayName] = [];
                }
                grouped[dayName].push(dateInfo);
            });
            
            // Return grouped object in day order
            const ordered = {};
            dayOrder.forEach(day => {
                if (grouped[day]) {
                    ordered[day] = grouped[day];
                }
            });
            
            return ordered;
        },
        groupedReplacementDates() {
            const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const grouped = {};
            
            this.replacementDates.forEach(dateInfo => {
                const dayName = dateInfo.day || dayjs(dateInfo.date).format('dddd');
                if (!grouped[dayName]) {
                    grouped[dayName] = [];
                }
                grouped[dayName].push(dateInfo);
            });
            
            // Return grouped object in day order
            const ordered = {};
            dayOrder.forEach(day => {
                if (grouped[day]) {
                    ordered[day] = grouped[day];
                }
            });
            
            return ordered;
        }
    },
    mounted() {
        this.loadScheduledDates();
    },
    methods: {
        getScheduleName(dateInfo) {
            const dayName = dateInfo.day || '';
            const time = dateInfo.time || '';
            
            if (!dayName || !time) {
                return dateInfo.scheduleName || 'Scheduled Session';
            }
            
            const [hour24, minutes] = time.split(':');
            const hour = parseInt(hour24);
            
            const period = hour < 12 ? 'AM' : 'PM';
            const hour12 = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
            
            return `${dayName + " Session"} (${hour12}:${minutes} ${period})`;
        },
        changeMonth(direction) {
            const newDate = dayjs(`${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}-01`).add(direction, 'month');
            this.currentYear = newDate.year();
            this.currentMonth = newDate.month() + 1;
            this.selectedOriginalDate = null;
            this.loadScheduledDates();
        },
        async loadScheduledDates() {
            this.loadingDates = true;
            try {
                // /available-dates already returns per-date swap status AND MC status,
                // so no separate /existing-makeups round-trip is needed here.
                const { data } = await this.$root.axios.get('/makeup_sessions/available-dates', {
                    params: {
                        userID: this.userId,
                        year: this.currentYear,
                        month: this.currentMonth
                    }
                });

                if (data.success) {
                    // A session is selectable as long as it's still ahead (backend minDays,
                    // default 1 = tomorrow onward). The replacement picker then only offers
                    // dates after this coming weekend.
                    const minDays = data.minDays ?? 1;
                    const today = dayjs().startOf('day');
                    this.scheduledDates = [];
                    this.excludedDates = Array.isArray(data.excluded) ? data.excluded : [];

                    data.schedules.forEach(schedule => {
                        schedule.dates.forEach(dateObj => {
                            // Handle both old format (string) and new format (object)
                            const dateStr = typeof dateObj === 'string' ? dateObj : dateObj.date;
                            const isSwapped = dateObj.isSwapped || false;
                            const swapInfo = dateObj.swapInfo || null;
                            const hasMC = dateObj.hasMC || false;

                            const sessionDate = dayjs(dateStr).startOf('day');
                            const daysDiff = sessionDate.diff(today, 'day');
                            // Show all future dates (including swapped ones and those with MC).
                            if (daysDiff >= minDays) {
                                this.scheduledDates.push({
                                    scheduleId: schedule.scheduleId,
                                    scheduleName: schedule.scheduleName,
                                    date: dateStr,
                                    canSwap: !isSwapped && !hasMC,
                                    isSwapped: isSwapped,
                                    hasMC: hasMC,
                                    swapInfo: swapInfo,
                                    day: schedule.dayName,
                                    time: schedule.time,
                                });
                            }
                        });
                    });
                    
                    // Sort by date
                    this.scheduledDates.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
                }
            } catch (error) {
                console.error('Error loading scheduled dates:', error);
                if (error.response?.data?.requiresGender === true) {
                    this.showGenderModal = true;
                    return;
                }
                this.showErrorModal('Error', 'Failed to load scheduled sessions. Please try again.');
            } finally {
                this.loadingDates = false;
            }
        },
        selectOriginalDate(dateInfo) {
            // Prevent selection of already swapped dates
            if (dateInfo.isSwapped) {
                this.showErrorModal('Already Swapped', 'This session has already been swapped and cannot be selected again.');
                return;
            }
            
            // Prevent selection of dates with MC/Other requests applied
            if (dateInfo.hasMC) {
                this.showErrorModal('MC Applied', 'This session has a medical certificate or other request submitted and cannot be swapped.');
                return;
            }
            
            // All displayed dates are already validated to be future (selectable) sessions.
            // Go straight to the replacement picker — no separate "next" button.
            this.selectedOriginalDate = dateInfo;
            this.selectedReplacementDate = null;
            this.step = 2;
        },
        async goBack() {
            this.step = 1;
            this.selectedReplacementDate = null;
            this.replacementDates = [];
            this.replacementRange = null;
            this.excludedReplacements = [];
        },
        selectReplacementDate(dateInfo) {
            // Prevent selection of fully booked dates
            if (dateInfo.isFullyBooked) {
                this.showErrorModal('Date Unavailable', 'This date is already booked for another swap and cannot be selected.');
                return;
            }
            this.selectedReplacementDate = dateInfo;
            this.askConfirmSwap();
        },
        // Tap-to-confirm: one dialog instead of a separate button.
        async askConfirmSwap() {
            const h = this.$createElement;
            const line = (label, date, sched) => h('div', { class: 'swap-confirm-line' }, [
                h('div', { class: 'swap-confirm-label' }, label),
                h('div', { class: 'swap-confirm-date' }, this.formatDate(date)),
                h('div', { class: 'swap-confirm-sched' }, sched || ''),
            ]);
            const body = h('div', [
                line('Missing', this.selectedOriginalDate.date, this.friendlySession(this.selectedOriginalDate)),
                h('div', { class: 'swap-confirm-arrow' }, '↓'),
                line('Attending instead', this.selectedReplacementDate.date, this.friendlySession(this.selectedReplacementDate)),
            ]);
            let ok = false;
            try {
                ok = await this.$bvModal.msgBoxConfirm([body], {
                    title: 'Confirm this swap?',
                    okTitle: 'Confirm swap',
                    okVariant: 'success',
                    cancelTitle: 'Back',
                    centered: true,
                    size: 'sm',
                    buttonSize: 'lg',
                    footerClass: 'swap-confirm-footer',
                });
            } catch (e) { ok = false; }
            if (ok) {
                await this.confirmSwap();
            } else {
                this.selectedReplacementDate = null;
            }
        },
        // Caregiver-facing session label: "Friday (2:00 PM) · English". The raw
        // schedule name ("Friday PM (ENGLISH) - MILD") is staff-only wording.
        friendlySession(info) {
            if (!info) return '';
            const day = info.day || dayjs(info.date).format('dddd');
            const time = info.time ? this.shortTime(info.time) : '';
            const lang = this.langWord(info.scheduleName);
            return `${day}${time ? ` (${time})` : ''}${lang ? ` · ${lang}` : ''}`;
        },
        langWord(name) {
            const m = /\b(CHINESE|ENGLISH|DIALECT|MALAY|TAMIL|MANDARIN|HOKKIEN|CANTONESE)\b/i.exec(name || '');
            if (!m) return '';
            const w = m[1].toUpperCase();
            return { CHINESE: 'Chinese', ENGLISH: 'English', DIALECT: 'Dialect', MALAY: 'Malay', TAMIL: 'Tamil', MANDARIN: 'Mandarin', HOKKIEN: 'Hokkien', CANTONESE: 'Cantonese' }[w];
        },
        // Language of the session, if the schedule name carries one (two sessions
        // can share a day and time — e.g. Friday PM Chinese vs English).
        langTag(name) {
            const m = /\b(CHINESE|ENGLISH|DIALECT|MALAY|TAMIL|MANDARIN|HOKKIEN|CANTONESE)\b/i.exec(name || '');
            if (!m) return '';
            const w = m[1].toUpperCase();
            return { CHINESE: 'CN', ENGLISH: 'EN', DIALECT: 'Dialect', MALAY: 'MY', TAMIL: 'TA', MANDARIN: 'CN', HOKKIEN: 'Hokkien', CANTONESE: 'Canto' }[w] || w;
        },
        async loadReplacementDates() {
            this.loadingReplacements = true;
            try {
                const originalDate = dayjs(this.selectedOriginalDate.date);

                // /replacement-dates already excludes the user's own booked/original
                // dates server-side, so a separate /existing-makeups call isn't needed.
                const { data } = await this.$root.axios.get('/makeup_sessions/replacement-dates', {
                    params: {
                        userID: this.userId,
                        scheduleID: this.selectedOriginalDate.scheduleId,
                        originalDate: this.selectedOriginalDate.date,
                        year: originalDate.year(),
                        month: originalDate.month() + 1
                    }
                });

                this.replacementRange = data.dateRange || null;
                this.excludedReplacements = Array.isArray(data.excluded) ? data.excluded : [];

                if (data.success && data.schedules) {
                    this.replacementDates = [];
                    data.schedules.forEach(schedule => {
                        schedule.dates.forEach(date => {
                            const bookingsForDate = schedule.bookingsByDate?.[date] || 0;
                            const maxCapacity = schedule.maxCapacity || 0;
                            const availableSlots = maxCapacity > 0 ? maxCapacity - bookingsForDate : 'Available';

                            const isFullyBooked = maxCapacity > 0 && bookingsForDate >= maxCapacity;

                            this.replacementDates.push({
                                scheduleId: schedule.scheduleId,
                                scheduleName: schedule.scheduleName,
                                date: date,
                                availableSlots: availableSlots,
                                isFullyBooked: isFullyBooked,
                                day: schedule.dayName,
                                time: schedule.time
                            });
                        });
                    });
                    
                    this.replacementDates.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());
                }
            } catch (error) {
                console.error('Error loading replacement dates:', error);
                this.showErrorModal('Error', error.response?.data?.error || error.response?.data?.message || 'Failed to load replacement dates.');
            } finally {
                this.loadingReplacements = false;
            }
        },
        async confirmSwap() {
            if (!this.selectedReplacementDate) return;
            
            this.submitting = true;
            try {
                const clientName = this.$store?.state?.cli_name || this.$root.currentUser[0]?.crb5c_client?.crb5c_name || 'Client';
                
                const { data } = await this.$root.axios.post('/makeup_sessions/create', {
                    userID: this.userId,
                    scheduleID: this.selectedOriginalDate.scheduleId,
                    originalDate: this.selectedOriginalDate.date,
                    replacementScheduleID: this.selectedReplacementDate.scheduleId,
                    replacementDate: this.selectedReplacementDate.date,
                    requestType: 0,
                    // Shown back to the caregiver under My Swaps — keep it in the friendly form.
                    reason: `Swapping from ${this.friendlySession(this.selectedOriginalDate)} to ${this.friendlySession(this.selectedReplacementDate)}`,
                    clientName: clientName,
                    status : 4
                });
                
                if (data && (data.message == "created")) {
                    this.step = 1;
                    this.selectedOriginalDate = null;
                    this.selectedReplacementDate = null;
                    this.replacementDates = [];
                    this.replacementRange = null;
                    this.excludedReplacements = [];
                    this.showSuccessModal = true;
                    this.loadScheduledDates();
                    // Emit event to notify parent to refresh data
                    this.$emit('request-submitted');
                }
            } catch (error) {
                console.error('Error creating swap request:', error);
                const status = error.response?.status;
                // Backend sends the human-readable message in `error` (not `message`)
                const serverMsg = error.response?.data?.error || error.response?.data?.message;

                // 400 (full capacity) / 409 (slot just taken) — the slot the user
                // picked is no longer available. Refresh the list and let them re-pick
                // from fresh data instead of leaving a stale, un-bookable selection.
                if (status === 400 || status === 409) {
                    this.selectedReplacementDate = null;
                    await this.loadReplacementDates();
                    this.showErrorModal('Slot Unavailable', serverMsg || 'That slot is no longer available. Please pick another date.');
                } else {
                    this.showErrorModal('Error', serverMsg || 'Failed to submit swap request.');
                }
            } finally {
                this.submitting = false;
            }
        },
        async saveGender() {
            if (this.selectedGender == null) return;
            this.savingGender = true;
            try {
                await this.$root.axios.post('/makeup_sessions/update-gender', {
                    userID: this.userId,
                    gender: this.selectedGender
                });
                this.showGenderModal = false;
                await this.loadScheduledDates();
            } catch (err) {
                console.error('Error saving gender:', err);
                this.showErrorModal('Error', 'Failed to save gender. Please try again.');
            } finally {
                this.savingGender = false;
            }
        },
        resetForm() {
            this.step = 1;
            this.selectedOriginalDate = null;
            this.selectedReplacementDate = null;
            this.replacementDates = [];
            this.replacementRange = null;
            this.excludedReplacements = [];
            this.showSuccessModal = false;
            this.loadScheduledDates();
        },
        formatDateMini(dateStr){
                return dayjs(dateStr).format('MMM D');
        },
        formatDate(dateStr) {
            return dayjs(dateStr).format('ddd, MMM D, YYYY');
        },
        shortDate(dateStr) {
            return dayjs(dateStr).format('D MMM');
        },
        shortTime(timeStr) {
            if (!timeStr) return 'Time TBC';
            const [h, m] = timeStr.split(':');
            const hour = parseInt(h, 10);
            // Schedules with no time set arrive as "Time not set" or with null minutes —
            // degrade gracefully instead of rendering "NaN:undefined PM".
            if (Number.isNaN(hour) || m == null || m === '') return 'Time TBC';
            const period = hour < 12 ? 'AM' : 'PM';
            const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            return `${h12}:${m} ${period}`;
        },
        dayPillStyle(dayName) {
            const colors = {
                Monday:    { bg: '#e8f0fe', color: '#1a73e8', border: '#1a73e8' },
                Tuesday:   { bg: '#fce8e6', color: '#d93025', border: '#d93025' },
                Wednesday: { bg: '#e6f4ea', color: '#188038', border: '#188038' },
                Thursday:  { bg: '#fff3e0', color: '#e65100', border: '#e65100' },
                Friday:    { bg: '#f3e8fd', color: '#8967B3', border: '#8967B3' },
                Saturday:  { bg: '#e8f5e9', color: '#00695c', border: '#00695c' },
                Sunday:    { bg: '#fce4ec', color: '#c2185b', border: '#c2185b' },
            };
            const c = colors[dayName] || { bg: '#f0f0f0', color: '#555', border: '#aaa' };
            return { backgroundColor: c.bg, color: c.color, borderColor: c.border };
        },
        dayAccentColor(dayName) {
            const colors = {
                Monday:    '#1a73e8',
                Tuesday:   '#d93025',
                Wednesday: '#188038',
                Thursday:  '#e65100',
                Friday:    '#8967B3',
                Saturday:  '#00695c',
                Sunday:    '#c2185b',
            };
            return colors[dayName] || '#8967B3';
        },
        chipAccent(dateInfo, dayName) {
            return { '--accent': this.dayAccentColor(dayName) };
        },
        chipClass(dateInfo, mode) {
            const classes = {};
            if (mode === 'original') {
                classes['chip--selected'] = !!(this.selectedOriginalDate &&
                    this.selectedOriginalDate.date === dateInfo.date &&
                    this.selectedOriginalDate.scheduleId === dateInfo.scheduleId);
                classes['chip--disabled'] = dateInfo.isSwapped || dateInfo.hasMC;
            } else {
                classes['chip--selected'] = !!(this.selectedReplacementDate &&
                    this.selectedReplacementDate.date === dateInfo.date &&
                    this.selectedReplacementDate.scheduleId === dateInfo.scheduleId);
                classes['chip--disabled'] = dateInfo.isFullyBooked;
            }
            return classes;
        },
    },
    watch: {
        step(newStep) {
            if (newStep === 2 && this.selectedOriginalDate) {
                this.loadReplacementDates();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$purple: #8967B3;
$radius-lg: 16px;
$radius-md: 12px;
$radius-sm: 8px;
$shadow-sm: 0 2px 8px rgba(0,0,0,0.08);
$shadow-md: 0 4px 16px rgba(0,0,0,0.12);
$transition: all 0.22s ease;

// Container
.swap-session-container {
    min-height: 55vh;
    background: #f7f6fb;
    border-radius: $radius-lg;
    overflow: hidden;
}

// Step bar
.step-bar {
    background: white;
    padding: 0.9rem 1.5rem;
    border-bottom: 1px solid #ede9f4;
}
.step-bar-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 280px;
    margin: 0 auto;
}
.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    .step-bubble {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: #e0d9f0;
        color: #888;
        font-size: 0.8rem;
        font-weight: 700;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: $transition;
    }
    .step-label {
        font-size: 0.65rem;
        color: #aaa;
        white-space: nowrap;
        transition: $transition;
    }
    &.active {
        .step-bubble { background: $purple; color: white; }
        .step-label  { color: $purple; font-weight: 600; }
    }
    &.done {
        .step-bubble { background: #d4edda; color: #155724; }
    }
}
.step-connector {
    flex: 1;
    height: 2px;
    background: #e0d9f0;
    margin: 0 8px;
    margin-bottom: 16px;
    border-radius: 2px;
    transition: $transition;
    &.active { background: $purple; }
}

// Step content
.step-content {
    padding: 1rem;
}

// Month pill
.month-pill {
    background: white;
    border-radius: $radius-md;
    padding: 0.65rem 1rem;
    box-shadow: $shadow-sm;
    .month-label {
        font-weight: 600;
        color: #333;
        font-size: 0.95rem;
    }
    .month-btn {
        border-radius: 20px;
        font-size: 0.78rem;
        padding: 0.2rem 0.75rem;
    }
}

// Replacement window hint (step 2)
.window-hint {
    display: flex;
    align-items: center;
    font-size: 0.78rem;
    color: #5a4b7a;
    background: #f0eaf8;
    border-radius: $radius-sm;
    padding: 0.5rem 0.75rem;
    strong { margin: 0 2px; }
}

// Scroll container
.dates-scroll-container {
    max-height: 52vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 2px;
    margin-bottom: 0.5rem;
    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #ccc; border-radius: 4px; }
}

// Day group header
.day-group-header {
    margin-bottom: 0.6rem;
}
.day-pill {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    padding: 0.2rem 0.75rem;
    border-radius: 20px;
    border: 1.5px solid currentColor;
}

// Horizontal date strip
.date-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    padding-bottom: 0.25rem;
}

// Date chip
.date-chip {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 80px;
    min-height: 82px;
    background: white;
    border-radius: $radius-md;
    border: 2px solid #e8e4f0;
    border-left: 4px solid var(--accent, #{$purple});
    box-shadow: $shadow-sm;
    cursor: pointer;
    padding: 0.45rem 0.4rem;
    transition: $transition;
    user-select: none;
    text-align: center;
    overflow: hidden;

    &:hover:not(.chip--disabled) {
        transform: translateY(-3px);
        box-shadow: $shadow-md;
        border-color: var(--accent, #{$purple});
    }

    &.chip--selected {
        background: var(--accent, #{$purple});
        border-color: var(--accent, #{$purple});
        color: white;
        box-shadow: 0 4px 14px rgba(0,0,0,0.18);
        .chip-date, .chip-time, .chip-slots { color: white !important; }
        .chip-slots { opacity: 0.85; }
    }

    &.chip--disabled {
        opacity: 0.45;
        cursor: not-allowed;
        background: #f2f2f2;
        border-left-color: #ccc;
    }
}

.chip-date {
    font-size: 0.82rem;
    font-weight: 700;
    color: #222;
    line-height: 1.1;
    margin-top: 2px;
}
.chip-lang {
    position: absolute;
    right: 6px;
    bottom: 6px;
    font-size: 0.6rem;
    font-weight: 800;
    letter-spacing: 0.03em;
    color: #50276b;
    background: #efe6f7;
    border-radius: 8px;
    padding: 1px 5px;
    line-height: 1.3;
}

.chip-time {
    font-size: 0.68rem;
    color: #888;
    margin-top: 2px;
    line-height: 1;
}
.chip-slots {
    font-size: 0.62rem;
    color: #555;
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.chip-status-icon {
    position: absolute;
    top: 4px;
    right: 5px;
    color: #aaa;
    line-height: 1;
}
.chip-check {
    position: absolute;
    top: 4px;
    right: 4px;
    color: white;
    line-height: 1;
}
.chip-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-align: center;
    padding: 2px 0;
    &--swapped { background: #dde3b7; color: #8b8b8b; }
    &--mc      { background: #fff3cd; color: #856404; }
    &--full    { background: #f8d7da; color: #721c24; }
}

// Empty state
.empty-state {
    text-align: center;
    padding: 3rem 1rem;
}
.empty-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: #ede9f4;
    border-radius: 50%;
    color: $purple;
    &--warning { background: #fff3cd; color: #856404; }
}

// CTA bar
.cta-bar {
    padding-top: 0.75rem;
    border-top: 1px solid #ede9f4;
    margin-top: 0.5rem;
}
.excluded-note {
    display: flex;
    align-items: flex-start;
    font-size: 0.75rem;
    line-height: 1.35;
    color: #7a6a8f;
    background: #f7f4fb;
    border-radius: $radius-sm;
    padding: 0.45rem 0.7rem;
    margin-top: 0.5rem;
    text-align: left;
    .b-icon { flex-shrink: 0; margin-top: 2px; }
}
.selected-summary {
    font-size: 0.85rem;
    color: #444;
    padding: 0.4rem 0.6rem;
    background: #f0eaf8;
    border-radius: $radius-sm;
    margin-bottom: 0.4rem;
    display: flex;
    align-items: center;
}
.cta-btn {
    border-radius: 24px;
    font-weight: 600;
    font-size: 0.95rem;
    padding: 0.6rem 1.5rem;
}

// Swap banner (step 2)
.swap-banner {
    background: white;
    border-radius: $radius-md;
    padding: 1rem;
    box-shadow: $shadow-sm;
}
.back-btn {
    color: $purple;
    font-size: 0.85rem;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.swap-from-to {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-top: 0.5rem;
}
.from-block, .to-block { flex: 1; }
.from-to-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #aaa;
}
.from-to-date {
    font-size: 0.85rem;
    font-weight: 700;
    color: #222;
    line-height: 1.2;
    &.placeholder-text { color: #b0a8c8; font-style: italic; font-weight: 400; }
}
.from-to-sched {
    font-size: 0.7rem;
    color: #888;
    margin-top: 1px;
}
.swap-arrow { color: $purple; flex-shrink: 0; }

// Confirm panel
.confirm-panel {
    background: white;
    border-radius: $radius-md;
    padding: 1rem;
    box-shadow: $shadow-sm;
    border-top: 3px solid #28a745;
}
.confirm-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
}
.confirm-col { flex: 1; }
.confirm-label {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #aaa;
}
.confirm-value {
    font-size: 0.85rem;
    font-weight: 700;
    color: #222;
}
.confirm-arrow { color: #28a745; flex-shrink: 0; }

// Utilities
.fw-600 { font-weight: 600; }
.fw-700 { font-weight: 700; }

// Slide-up transition
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.25s ease; }
.slide-up-enter, .slide-up-leave-to { opacity: 0; transform: translateY(10px); }
</style>
